import { useState, useEffect, useRef, useCallback } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from '../common/Toast/toast';
import { useNavigate, useParams } from "react-router-dom";
import { getMe } from "../../../services/AuthService";
import { getTrainers ,getTrainerByUserId  } from "../../../services/TrainerService";
import { getcourses } from "../../../services/CourseModule";
import { getQuizzesWithFilters } from "../../../services/QuizService.js";
import { useAuth } from "../../../context/AuthContext";
import { getBatchesWithFilters } from "../../../services/BatchService";
import {
  createBatchQuizHistory,
  updateBatchQuizHistory,
  getBatchQuizHistoryById,
} from "../../../services/BatchQuizHistoryService.js";
import CustomAsyncDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
import "./TrainerDashboard.css";

// Formik holds these as Date objects and the submit handler calls
// toISOString on them; <input type="datetime-local"> speaks wall-clock
// text. The two are converted at the edge rather than anywhere else.
const toLocalInput = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d;
};

// Rendered on its own route, and inside the assignments list as a dialog.
// `recordId` stands in for the route param when embedded, and onDone /
// onCancel replace the navigation the page version does.
function AssignQuizToBatch({ embedded = false, recordId, onDone, onCancel }) {
  const { id: routeId } = useParams();
  const id = recordId || routeId;
  const [assignmentData, setAssignmentData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Dropdown options
  const [trainerOptions, setTrainerOptions] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [quizOptions, setQuizOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);

  // Pagination states for courses
  const [coursePage, setCoursePage] = useState(1);
  const [hasMoreCourses, setHasMoreCourses] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [courseHasLoaded, setCourseHasLoaded] = useState(false);
  const isFetchingCourses = useRef(false);

  // Pagination states for trainers
  const [trainerPage, setTrainerPage] = useState(1);
  const [hasMoreTrainers, setHasMoreTrainers] = useState(true);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [trainerSearchTerm, setTrainerSearchTerm] = useState("");
  const [trainerHasLoaded, setTrainerHasLoaded] = useState(false);
  const isFetchingTrainers = useRef(false);

  // Pagination states for quizzes
  const [quizPage, setQuizPage] = useState(1);
  const [hasMoreQuizzes, setHasMoreQuizzes] = useState(true);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [quizSearchTerm, setQuizSearchTerm] = useState("");
  const [quizHasLoaded, setQuizHasLoaded] = useState(false);
  const isFetchingQuizzes = useRef(false);
  const isFetchingQuizzesMore = useRef(false);

  // Pagination states for batches
  const [batchPage, setBatchPage] = useState(1);
  const [hasMoreBatches, setHasMoreBatches] = useState(true);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchSearchTerm, setBatchSearchTerm] = useState("");
  const [batchHasLoaded, setBatchHasLoaded] = useState(false);
  const isFetchingBatches = useRef(false);
  const isFetchingBatchesMore = useRef(false);

  // User data
  const [trainerId, setTrainerId] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();
  const getMeCalled = useRef(false);

  useEffect(() => {
    if (!hasPermission("list-quiz-to-batch", "add", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to assign quizzes to batch.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // Fetch courses with pagination
  const fetchCourses = useCallback(
    async (page = 1, search = "") => {
      if (loadingCourses || !hasMoreCourses || isFetchingCourses.current) return;

      isFetchingCourses.current = true;
      setLoadingCourses(true);
      try {
        const response = await getcourses(20, page, search);
        const coursesData = response.data || [];

        const newOptions = coursesData.map((course) => ({
          value: course.id,
          label: `${course.name} (${course.code})`,
        }));

        if (page === 1) {
          setCourseOptions(newOptions);
        } else {
          setCourseOptions((prev) => [...prev, ...newOptions]);
        }

        setHasMoreCourses(coursesData.length === 20);
        setCoursePage(page);
      } catch (error) {
        console.error("Error fetching courses:", error);
        toast.error("Error loading courses");
      } finally {
        setLoadingCourses(false);
        isFetchingCourses.current = false;
      }
    },
    [loadingCourses, hasMoreCourses]
  );

  // Fetch trainers with pagination
  const fetchTrainers = useCallback(
    async (page = 1, search = "") => {
      if (loadingTrainers || !hasMoreTrainers || isFetchingTrainers.current) return;

      isFetchingTrainers.current = true;
      setLoadingTrainers(true);
      try {
        const response = await getTrainers(20, page, search);
        const trainersData = response.data || [];

        const options = trainersData.map((trainer) => {
          const name = trainer.trainerName || "";
          const email = trainer.traineremail || trainer.email || "";
          const label = email ? `${name} (${email})` : name || `Trainer ${trainer.id}`;
          return {
            value: trainer.id,
            label,
            email,
          };
        });

        if (page === 1) {
          setTrainerOptions(options);
        } else {
          setTrainerOptions((prev) => [...prev, ...options]);
        }

        setHasMoreTrainers(trainersData.length === 20);
        setTrainerPage(page);
      } catch (error) {
        console.error("Error fetching trainers:", error);
        toast.error("Error loading trainers");
      } finally {
        setLoadingTrainers(false);
        isFetchingTrainers.current = false;
      }
    },
    [loadingTrainers, hasMoreTrainers]
  );

  // Fetch quizzes by course ID with pagination and trainer filter
  const fetchQuizzesByCourse = useCallback(
    async (courseId, page = 1, search = "", isLoadMore = false, trainerIdParam = null) => {
      if (!courseId) return;
      
      if (isLoadMore && (isFetchingQuizzesMore.current || !hasMoreQuizzes)) return;
      if (!isLoadMore && isFetchingQuizzes.current) return;

      if (isLoadMore) {
        isFetchingQuizzesMore.current = true;
        setLoadingQuizzes(true);
      } else {
        isFetchingQuizzes.current = true;
        setLoadingQuizzes(true);
      }

      try {
        let createdByParam = "";
        if (trainerIdParam !== null) {
          createdByParam = trainerIdParam;
        } else if (isTrainer && trainerId) {
          createdByParam = trainerId;
        } else if (isAdmin && selectedTrainer?.value) {
          createdByParam = selectedTrainer.value;
        }
        
        const response = await getQuizzesWithFilters(
          20, page, search, "", courseId, createdByParam
        );
        
        const quizzesData = response.data || [];
        const newOptions = quizzesData.map((quiz) => ({
          value: quiz.id,
          label: quiz.title,
        }));

        if (isLoadMore) {
          setQuizOptions((prev) => {
            const existingIds = new Set(prev.map(opt => opt.value));
            const uniqueNewOptions = newOptions.filter(opt => !existingIds.has(opt.value));
            return [...prev, ...uniqueNewOptions];
          });
        } else {
          setQuizOptions(newOptions);
          setQuizPage(1);
        }

        setHasMoreQuizzes(quizzesData.length === 20);
        setQuizPage(page);
      } catch (error) {
        console.error("Error fetching quizzes:", error);
        toast.error("Error loading quizzes");
      } finally {
        setLoadingQuizzes(false);
        if (isLoadMore) {
          isFetchingQuizzesMore.current = false;
        } else {
          isFetchingQuizzes.current = false;
        }
      }
    },
    [hasMoreQuizzes, loadingQuizzes, isTrainer, trainerId, isAdmin, selectedTrainer]
  );

  // Fetch batches by course ID with pagination and trainer filter
  const fetchBatchesByCourse = useCallback(
    async (courseId, page = 1, search = "", isLoadMore = false, trainerIdParam = null) => {
      if (!courseId) return;
      
      if (isLoadMore && (isFetchingBatchesMore.current || !hasMoreBatches)) return;
      if (!isLoadMore && isFetchingBatches.current) return;

      if (isLoadMore) {
        isFetchingBatchesMore.current = true;
        setLoadingBatches(true);
      } else {
        isFetchingBatches.current = true;
        setLoadingBatches(true);
      }

      try {
        let trainerIdParamToUse = "";
        if (trainerIdParam !== null) {
          trainerIdParamToUse = trainerIdParam;
        } else if (isTrainer && trainerId) {
          trainerIdParamToUse = trainerId;
        } else if (isAdmin && selectedTrainer?.value) {
          trainerIdParamToUse = selectedTrainer.value;
        }
        
        const response = await getBatchesWithFilters(20, page, search, courseId, trainerIdParamToUse);
        let batchesData = response.data || [];

        const newOptions = batchesData.map((batch) => ({
          value: batch.id,
          label: batch.name,
        }));

        if (isLoadMore) {
          setBatchOptions((prev) => {
            const existingIds = new Set(prev.map(opt => opt.value));
            const uniqueNewOptions = newOptions.filter(opt => !existingIds.has(opt.value));
            return [...prev, ...uniqueNewOptions];
          });
        } else {
          setBatchOptions(newOptions);
          setBatchPage(1);
        }

        setHasMoreBatches(batchesData.length === 20);
        setBatchPage(page);
      } catch (error) {
        console.error("Error fetching batches:", error);
        toast.error("Error loading batches");
      } finally {
        setLoadingBatches(false);
        if (isLoadMore) {
          isFetchingBatchesMore.current = false;
        } else {
          isFetchingBatches.current = false;
        }
      }
    },
    [hasMoreBatches, loadingBatches, isTrainer, trainerId, isAdmin, selectedTrainer]
  );

  // Handle dropdown open events
  const handleCourseDropdownOpen = useCallback(() => {
    if (!courseHasLoaded && !loadingCourses && courseOptions.length === 0) {
      setCourseHasLoaded(true);
      fetchCourses(1, "");
    }
  }, [courseHasLoaded, loadingCourses, fetchCourses, courseOptions.length]);

  const handleTrainerDropdownOpen = useCallback(() => {
    if (!trainerHasLoaded && !loadingTrainers && isAdmin && trainerOptions.length === 0) {
      setTrainerHasLoaded(true);
      fetchTrainers(1, "");
    }
  }, [trainerHasLoaded, loadingTrainers, isAdmin, fetchTrainers, trainerOptions.length]);

  const handleQuizDropdownOpen = useCallback(() => {
    if (selectedCourse?.value && !quizHasLoaded && !loadingQuizzes && quizOptions.length === 0) {
      setQuizHasLoaded(true);
      fetchQuizzesByCourse(selectedCourse.value, 1, "", false);
    }
  }, [selectedCourse, quizHasLoaded, loadingQuizzes, fetchQuizzesByCourse, quizOptions.length]);

  const handleBatchDropdownOpen = useCallback(() => {
    if (selectedCourse?.value && !batchHasLoaded && !loadingBatches && batchOptions.length === 0) {
      setBatchHasLoaded(true);
      fetchBatchesByCourse(selectedCourse.value, 1, "", false);
    }
  }, [selectedCourse, batchHasLoaded, loadingBatches, fetchBatchesByCourse, batchOptions.length]);

  // Handle search functions
  const handleCourseSearch = useCallback(
    (searchTerm) => {
      setCourseSearchTerm(searchTerm);
      setCourseHasLoaded(true);
      setCoursePage(1);
      setHasMoreCourses(true);
      fetchCourses(1, searchTerm);
    },
    [fetchCourses]
  );

  const handleTrainerSearch = useCallback(
    (searchTerm) => {
      if (isAdmin) {
        setTrainerSearchTerm(searchTerm);
        setTrainerHasLoaded(true);
        setTrainerPage(1);
        setHasMoreTrainers(true);
        fetchTrainers(1, searchTerm);
      }
    },
    [isAdmin, fetchTrainers]
  );

  const handleQuizSearch = useCallback(
    (searchTerm) => {
      if (selectedCourse?.value) {
        setQuizSearchTerm(searchTerm);
        setQuizHasLoaded(true);
        setQuizPage(1);
        setHasMoreQuizzes(true);
        fetchQuizzesByCourse(selectedCourse.value, 1, searchTerm, false);
      }
    },
    [selectedCourse, fetchQuizzesByCourse]
  );

  const handleBatchSearch = useCallback(
    (searchTerm) => {
      if (selectedCourse?.value) {
        setBatchSearchTerm(searchTerm);
        setBatchHasLoaded(true);
        setBatchPage(1);
        setHasMoreBatches(true);
        fetchBatchesByCourse(selectedCourse.value, 1, searchTerm, false);
      }
    },
    [selectedCourse, fetchBatchesByCourse]
  );

  // Handle load more functions
  const handleLoadMoreCourses = useCallback(() => {
    if (hasMoreCourses && !loadingCourses && !isFetchingCourses.current) {
      fetchCourses(coursePage + 1, courseSearchTerm);
    }
  }, [hasMoreCourses, loadingCourses, fetchCourses, coursePage, courseSearchTerm]);

  const handleLoadMoreTrainers = useCallback(() => {
    if (hasMoreTrainers && !loadingTrainers && !isFetchingTrainers.current && isAdmin) {
      fetchTrainers(trainerPage + 1, trainerSearchTerm);
    }
  }, [hasMoreTrainers, loadingTrainers, isAdmin, fetchTrainers, trainerPage, trainerSearchTerm]);

  const handleLoadMoreQuizzes = useCallback(() => {
    if (selectedCourse?.value && hasMoreQuizzes && !loadingQuizzes && !isFetchingQuizzesMore.current) {
      fetchQuizzesByCourse(selectedCourse.value, quizPage + 1, quizSearchTerm, true);
    }
  }, [selectedCourse, hasMoreQuizzes, loadingQuizzes, fetchQuizzesByCourse, quizPage, quizSearchTerm]);

  const handleLoadMoreBatches = useCallback(() => {
    if (selectedCourse?.value && hasMoreBatches && !loadingBatches && !isFetchingBatchesMore.current) {
      fetchBatchesByCourse(selectedCourse.value, batchPage + 1, batchSearchTerm, true);
    }
  }, [selectedCourse, hasMoreBatches, loadingBatches, fetchBatchesByCourse, batchPage, batchSearchTerm]);

  // Refresh functions
  const handleRefreshCourses = useCallback(async () => {
    setCourseSearchTerm("");
    setCoursePage(1);
    setHasMoreCourses(true);
    setCourseHasLoaded(false);
    setCourseOptions([]);
    await fetchCourses(1, "");
  }, [fetchCourses]);

  const handleRefreshTrainers = useCallback(async () => {
    if (!isAdmin) return;
    setTrainerSearchTerm("");
    setTrainerPage(1);
    setHasMoreTrainers(true);
    setTrainerHasLoaded(false);
    setTrainerOptions([]);
    await fetchTrainers(1, "");
  }, [isAdmin, fetchTrainers]);

  const handleRefreshQuizzes = useCallback(async () => {
    if (selectedCourse?.value) {
      setQuizSearchTerm("");
      setQuizPage(1);
      setHasMoreQuizzes(true);
      setQuizHasLoaded(false);
      setQuizOptions([]);
      await fetchQuizzesByCourse(selectedCourse.value, 1, "");
    }
  }, [selectedCourse, fetchQuizzesByCourse]);

  const handleRefreshBatches = useCallback(async () => {
    if (selectedCourse?.value) {
      setBatchSearchTerm("");
      setBatchPage(1);
      setHasMoreBatches(true);
      setBatchHasLoaded(false);
      setBatchOptions([]);
      await fetchBatchesByCourse(selectedCourse.value, 1, "");
    }
  }, [selectedCourse, fetchBatchesByCourse]);

  // Get current trainer name for read-only display
  const getCurrentTrainerName = () => {
    if (!currentUser) return "";
    const firstName = currentUser.firstName || "";
    const lastName = currentUser.lastName || "";
    const email = currentUser.email || "";

    if (firstName || lastName) {
      let name = `${firstName} ${lastName}`.trim();
      if (email) {
        name += ` (${email})`;
      }
      return name;
    }
    return email;
  };

  // Initialize component with user data
  const initializeComponent = useCallback(async () => {
    if (getMeCalled.current) return;
    getMeCalled.current = true;

    try {
      setLoading(true);
      const userProfile = await getMe();
      const userData = userProfile.data;
      const userId = userData.id || userData._id || userData.userId;

      if (!userId) {
        toast.error("Unable to fetch user information");
        return;
      }

      setCurrentUser(userData);

      const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
      const isTrainerUser = profileLabel === "trainer";
      const isAdminUser = profileLabel === "admin" || userData.isSuperAdmin;

      setIsTrainer(isTrainerUser);
      setIsAdmin(isAdminUser);

      if (isTrainerUser) {
        try {
          const trainerResponse = await getTrainerByUserId(userId);
          const trainerData = trainerResponse.data;
          const actualTrainerId = trainerData.id || trainerData._id;
          
          if (actualTrainerId) {
            setTrainerId(actualTrainerId);
            const trainerName = trainerData.trainerName || "";
            const trainerEmail = trainerData.traineremail || trainerData.email || "";
            const label = trainerEmail ? `${trainerName} (${trainerEmail})` : trainerName || getCurrentTrainerName(userData);
            
            setSelectedTrainer({
              value: actualTrainerId,
              label: label,
            });
          }
        } catch (err) {
          console.error("Error fetching trainer data:", err);
        }
      }

      await fetchCourses(1, "");

      if (id) {
        await fetchAssignmentDetails(id);
      }
    } catch (err) {
      console.error("Error initializing component:", err);
      toast.error("Failed to initialize component");
    } finally {
      setLoading(false);
    }
  }, [id, fetchCourses]);

  // Fetch assignment details - NOW POPULATES DIRECTLY FROM RESPONSE WITHOUT EXTRA API CALLS
  const fetchAssignmentDetails = async (assignmentId) => {
    try {
      const response = await getBatchQuizHistoryById(assignmentId);
      if (response.data) {
        const assignment = response.data;
        
        // Extract trainer from the response (trainerId is an array with trainer object)
        let trainerIdValue = "";
        let trainerOption = null;
        if (assignment.trainerId && Array.isArray(assignment.trainerId) && assignment.trainerId.length > 0) {
          const trainer = assignment.trainerId[0];
          trainerIdValue = trainer.id;
          trainerOption = {
            value: trainer.id,
            label: `${trainer.trainerName} (${trainer.trainerEmail})`,
          };
          setSelectedTrainer(trainerOption);
        }
        
        // Extract course from the response (courseId is an array with course object)
        let courseIdValue = "";
        let courseOption = null;
        if (assignment.courseId && Array.isArray(assignment.courseId) && assignment.courseId.length > 0) {
          const course = assignment.courseId[0];
          courseIdValue = course.id;
          courseOption = {
            value: course.id,
            label: `${course.name} (${course.code || ""})`,
          };
          setSelectedCourse(courseOption);
          // Add to course options for dropdown
          setCourseOptions(prev => {
            const exists = prev.some(opt => opt.value === course.id);
            if (!exists) {
              return [courseOption, ...prev];
            }
            return prev;
          });
        }
        
        // Extract quiz from the response (quizId is an array with quiz object)
        let quizIdValue = "";
        let quizOption = null;
        if (assignment.quizId && Array.isArray(assignment.quizId) && assignment.quizId.length > 0) {
          const quiz = assignment.quizId[0];
          quizIdValue = quiz.id;
          quizOption = {
            value: quiz.id,
            label: quiz.title,
          };
          // Add to quiz options for dropdown
          setQuizOptions(prev => {
            const exists = prev.some(opt => opt.value === quiz.id);
            if (!exists) {
              return [quizOption, ...prev];
            }
            return prev;
          });
        }
        
        // Extract batches from the response (batchIds is an array with batch objects)
        let batchIdsArray = [];
        let batchOptionsList = [];
        if (assignment.batchIds && Array.isArray(assignment.batchIds) && assignment.batchIds.length > 0) {
          batchOptionsList = assignment.batchIds.map((batch) => ({
            value: batch.id,
            label: batch.name,
          }));
          batchIdsArray = assignment.batchIds.map((batch) => batch.id);
          // Add to batch options for dropdown
          setBatchOptions(batchOptionsList);
        }
        
        // Set the assignment data for form initial values
        const normalized = {
          trainerId: trainerIdValue,
          courseId: courseIdValue,
          quizId: quizIdValue,
          batchIds: batchIdsArray,
          startDate: assignment.startDate ? new Date(assignment.startDate) : new Date(),
          endDate: assignment.endDate ? new Date(assignment.endDate) : new Date(),
        };
        
        setAssignmentData(normalized);
        setIsEditing(true);
        
        // Mark dropdowns as loaded so they don't fetch again
        setCourseHasLoaded(true);
        setQuizHasLoaded(true);
        setBatchHasLoaded(true);
        if (isAdmin && trainerOption) {
          setTrainerHasLoaded(true);
        }
      } else {
        setAssignmentData(null);
        setIsEditing(false);
        toast.error("Assignment not found!");
      }
    } catch (error) {
      console.error("Error fetching assignment details:", error);
      toast.error("Failed to load assignment details");
      setAssignmentData(null);
      setIsEditing(false);
    }
  };

  // Handle course selection
  const handleCourseChange = useCallback(
    async (selectedCourseOption, setFieldValue) => {
      const courseId = selectedCourseOption ? selectedCourseOption.value : "";
      setSelectedCourse(selectedCourseOption);
      setFieldValue("courseId", courseId);

      if (courseId) {
        setFieldValue("quizId", "");
        setFieldValue("batchIds", []);
        setQuizOptions([]);
        setBatchOptions([]);
        setQuizHasLoaded(false);
        setBatchHasLoaded(false);
        setQuizPage(1);
        setBatchPage(1);
        setHasMoreQuizzes(true);
        setHasMoreBatches(true);
        setQuizSearchTerm("");
        setBatchSearchTerm("");
        
        isFetchingQuizzes.current = false;
        isFetchingQuizzesMore.current = false;
        isFetchingBatches.current = false;
        isFetchingBatchesMore.current = false;
        
        let currentTrainerId = "";
        if (isTrainer && trainerId) {
          currentTrainerId = trainerId;
        } else if (isAdmin && selectedTrainer?.value) {
          currentTrainerId = selectedTrainer.value;
        }
        
        await Promise.all([
          fetchQuizzesByCourse(courseId, 1, "", false, currentTrainerId),
          fetchBatchesByCourse(courseId, 1, "", false, currentTrainerId)
        ]);
      } else {
        setQuizOptions([]);
        setBatchOptions([]);
        setFieldValue("quizId", "");
        setFieldValue("batchIds", []);
      }
    },
    [fetchQuizzesByCourse, fetchBatchesByCourse, isTrainer, trainerId, isAdmin, selectedTrainer]
  );

  // Handle trainer selection
  const handleTrainerChange = useCallback(
    (selectedTrainerOption, setFieldValue) => {
      const newTrainerId = selectedTrainerOption ? selectedTrainerOption.value : "";
      setSelectedTrainer(selectedTrainerOption);
      setFieldValue("trainerId", newTrainerId);
      
      if (selectedCourse?.value) {
        setQuizOptions([]);
        setBatchOptions([]);
        setQuizHasLoaded(false);
        setBatchHasLoaded(false);
        setQuizPage(1);
        setBatchPage(1);
        setHasMoreQuizzes(true);
        setHasMoreBatches(true);
        setQuizSearchTerm("");
        setBatchSearchTerm("");
        
        isFetchingQuizzes.current = false;
        isFetchingQuizzesMore.current = false;
        isFetchingBatches.current = false;
        isFetchingBatchesMore.current = false;
        
        setTimeout(() => {
          Promise.all([
            fetchQuizzesByCourse(selectedCourse.value, 1, "", false, newTrainerId),
            fetchBatchesByCourse(selectedCourse.value, 1, "", false, newTrainerId)
          ]);
        }, 0);
      }
    },
    [selectedCourse, fetchQuizzesByCourse, fetchBatchesByCourse]
  );

  // Handle quiz selection
  const handleQuizChange = useCallback(
    (selectedQuizOption, setFieldValue) => {
      setFieldValue("quizId", selectedQuizOption ? selectedQuizOption.value : "");
    },
    []
  );

  // Handle batch selection for multi-select
  const handleBatchChange = useCallback(
    (selectedOptions, setFieldValue) => {
      if (selectedOptions && selectedOptions.length > 0) {
        const batchIds = selectedOptions.map((option) => option.value);
        setFieldValue("batchIds", batchIds);
      } else {
        setFieldValue("batchIds", []);
      }
    },
    []
  );

  const validationSchema = Yup.object().shape({
    trainerId: Yup.string().required("Trainer is required"),
    courseId: Yup.string().required("Course is required"),
    batchIds: Yup.array().min(1, "At least one batch is required"),
    quizId: Yup.string().required("Quiz is required"),
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date must be after start date"),
  });

  const initialValues = assignmentData
    ? {
        trainerId: assignmentData.trainerId,
        courseId: assignmentData.courseId,
        batchIds: Array.isArray(assignmentData.batchIds) ? assignmentData.batchIds : [],
        quizId: assignmentData.quizId,
        startDate: assignmentData.startDate ? new Date(assignmentData.startDate) : new Date(),
        endDate: assignmentData.endDate ? new Date(assignmentData.endDate) : new Date(),
      }
    : {
        trainerId: isTrainer ? trainerId : "",
        courseId: "",
        batchIds: [],
        quizId: "",
        startDate: new Date(),
        endDate: new Date(),
      };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const submissionData = {
        trainerId: isTrainer ? trainerId : values.trainerId,
        courseId: values.courseId,
        batchIds: values.batchIds.join(","),
        quizId: values.quizId,
        startDate: new Date(values.startDate).toISOString(),
        endDate: new Date(values.endDate).toISOString(),
        createdBy: isTrainer ? trainerId : values.trainerId,
        updatedBy: isTrainer ? trainerId : values.trainerId,
      };

      if (isEditing) {
        await updateBatchQuizHistory(id, submissionData);
        toast.success("Quiz assignment updated successfully!");
      } else {
        await createBatchQuizHistory(submissionData);
        toast.success("Quiz assigned to batch successfully!");
      }

      resetForm();
      setAssignmentData(null);
      setIsEditing(false);

      if (embedded) {
        onDone?.();
        return;
      }
      setTimeout(() => {
        navigate("/list-quiz-to-batch");
      }, 1500);
    } catch (error) {
      console.error("Error processing assignment:", error);
      if (error.response && error.response.status === 409) {
        toast.error("This assignment already exists!");
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Error processing assignment!");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Load trainers for admin when component mounts
  useEffect(() => {
    if (isAdmin && trainerOptions.length === 0 && !trainerHasLoaded) {
      fetchTrainers(1, "");
    }
  }, [isAdmin, trainerOptions.length, trainerHasLoaded, fetchTrainers]);

  useEffect(() => {
    initializeComponent();
  }, [initializeComponent]);

  if (loading) {
    return (
      <div className="att-picklist-foot">
        <span className="pg-spinner att-spinner" aria-hidden="true" />
        <span>Loading assignment...</span>
      </div>
    );
  }

  const leave = () => {
    if (embedded) onCancel?.();
    else navigate("/list-quiz-to-batch");
  };

  return (
    <div className={embedded ? "att-embed" : "pg"}>
      {!embedded && (
        <header className="pg-head">
          <div className="pg-head-main">
            <h4 className="pg-title">
              {isEditing ? "Edit assignment" : "Assign quiz to batch"}
            </h4>
          </div>
          <div className="pg-head-actions">
            <button type="button" className="pg-btn" onClick={leave}>
              Back to assignments
            </button>
          </div>
        </header>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={isEditing}
      >
        {({
          values,
          handleSubmit,
          setFieldValue,
          errors,
          touched,
          isSubmitting,
        }) => {
          const err = (f) => (touched[f] ? errors[f] : "");
          const ready =
            (isTrainer || String(values.trainerId || "").trim()) &&
            String(values.courseId || "").trim() &&
            String(values.quizId || "").trim() &&
            Array.isArray(values.batchIds) &&
            values.batchIds.length > 0 &&
            values.startDate &&
            values.endDate &&
            !errors.startDate &&
            !errors.endDate;

          return (
            <form className="att-form" noValidate onSubmit={handleSubmit}>
              <div className="att-stage">
                <div className="att-stage-inner">
                  {/* One step: six answers, asked once. Splitting them
                      across tabs would put two fields behind a Continue
                      button for no gain. */}
                  <section className="att-step">
                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label">Trainer</label>
                        {isTrainer ? (
                          <input
                            type="text"
                            className="pg-input"
                            value={getCurrentTrainerName()}
                            readOnly
                            disabled
                          />
                        ) : (
                          <CustomAsyncDropdown
                            options={trainerOptions}
                            value={selectedTrainer ? [selectedTrainer] : []}
                            onChange={(opts) =>
                              handleTrainerChange(
                                Array.isArray(opts) && opts.length
                                  ? opts[opts.length - 1]
                                  : null,
                                setFieldValue,
                              )
                            }
                            placeholder={
                              loadingTrainers
                                ? "Loading trainers..."
                                : "Select a trainer"
                            }
                            isMulti={false}
                            isSearchable={true}
                            searchInControl={true}
                            onMenuOpen={handleTrainerDropdownOpen}
                            onInputChange={handleTrainerSearch}
                            onMenuScrollToBottom={handleLoadMoreTrainers}
                            onRefresh={handleRefreshTrainers}
                            isLoading={loadingTrainers}
                          />
                        )}
                        {err("trainerId") && (
                          <p className="att-error">{err("trainerId")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label">Course</label>
                        <CustomAsyncDropdown
                          options={courseOptions}
                          value={selectedCourse ? [selectedCourse] : []}
                          onChange={(opts) =>
                            handleCourseChange(
                              Array.isArray(opts) && opts.length
                                ? opts[opts.length - 1]
                                : null,
                              setFieldValue,
                            )
                          }
                          placeholder={
                            loadingCourses
                              ? "Loading courses..."
                              : "Select a course"
                          }
                          isMulti={false}
                          isSearchable={true}
                          searchInControl={true}
                          onMenuOpen={handleCourseDropdownOpen}
                          onInputChange={handleCourseSearch}
                          onMenuScrollToBottom={handleLoadMoreCourses}
                          onRefresh={handleRefreshCourses}
                          isLoading={loadingCourses}
                        />
                        {err("courseId") && (
                          <p className="att-error">{err("courseId")}</p>
                        )}
                      </div>
                    </div>

                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label">Quiz</label>
                        <CustomAsyncDropdown
                          options={quizOptions}
                          value={quizOptions.filter(
                            (o) => o.value === values.quizId,
                          )}
                          onChange={(opts) =>
                            handleQuizChange(
                              Array.isArray(opts) && opts.length
                                ? opts[opts.length - 1]
                                : null,
                              setFieldValue,
                            )
                          }
                          placeholder={
                            loadingQuizzes
                              ? "Loading quizzes..."
                              : selectedCourse?.value
                                ? quizOptions.length > 0
                                  ? "Select a quiz"
                                  : "No quizzes on this course"
                                : "Choose a course first"
                          }
                          isMulti={false}
                          isSearchable={true}
                          searchInControl={true}
                          isDisabled={!selectedCourse?.value || loadingQuizzes}
                          onMenuOpen={handleQuizDropdownOpen}
                          onInputChange={handleQuizSearch}
                          onMenuScrollToBottom={handleLoadMoreQuizzes}
                          onRefresh={handleRefreshQuizzes}
                          isLoading={loadingQuizzes}
                        />
                        {err("quizId") && (
                          <p className="att-error">{err("quizId")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label">
                          Batches
                          <span className="att-card-count">
                            {values.batchIds?.length || 0} chosen
                          </span>
                        </label>
                        <CustomAsyncDropdown
                          options={batchOptions}
                          value={batchOptions.filter(
                            (o) =>
                              Array.isArray(values.batchIds) &&
                              values.batchIds.includes(o.value),
                          )}
                          onChange={(opts) =>
                            handleBatchChange(opts, setFieldValue)
                          }
                          placeholder={
                            loadingBatches
                              ? "Loading batches..."
                              : selectedCourse?.value
                                ? batchOptions.length > 0
                                  ? "Select batches"
                                  : isTrainer
                                    ? "No batches on this course are yours"
                                    : "No batches on this course"
                                : "Choose a course first"
                          }
                          isMulti={true}
                          isSearchable={true}
                          searchInControl={true}
                          isDisabled={!selectedCourse?.value || loadingBatches}
                          onMenuOpen={handleBatchDropdownOpen}
                          onInputChange={handleBatchSearch}
                          onMenuScrollToBottom={handleLoadMoreBatches}
                          onRefresh={handleRefreshBatches}
                          isLoading={loadingBatches}
                        />
                        {err("batchIds") && (
                          <p className="att-error">{err("batchIds")}</p>
                        )}
                      </div>
                    </div>

                    <div className="mcq-options">
                      <div className="pg-field">
                        <label className="pg-label" htmlFor="aqb-start">
                          Opens
                        </label>
                        <input
                          id="aqb-start"
                          type="datetime-local"
                          className="pg-input"
                          value={toLocalInput(values.startDate)}
                          onChange={(e) =>
                            setFieldValue(
                              "startDate",
                              fromLocalInput(e.target.value),
                            )
                          }
                        />
                        {err("startDate") && (
                          <p className="att-error">{err("startDate")}</p>
                        )}
                      </div>

                      <div className="pg-field">
                        <label className="pg-label" htmlFor="aqb-end">
                          Closes
                        </label>
                        <input
                          id="aqb-end"
                          type="datetime-local"
                          className="pg-input"
                          min={toLocalInput(values.startDate) || undefined}
                          value={toLocalInput(values.endDate)}
                          onChange={(e) =>
                            setFieldValue(
                              "endDate",
                              fromLocalInput(e.target.value),
                            )
                          }
                        />
                        {err("endDate") && (
                          <p className="att-error">{err("endDate")}</p>
                        )}
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              <div className="att-actions">
                <button type="button" className="pg-btn" onClick={leave}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pg-btn pg-btn--main"
                  disabled={isSubmitting || !ready}
                >
                  {isSubmitting ? (
                    <>
                      <span
                        className="pg-spinner att-spinner"
                        aria-hidden="true"
                      />
                      {isEditing ? "Updating..." : "Assigning..."}
                    </>
                  ) : isEditing ? (
                    "Update assignment"
                  ) : (
                    "Assign quiz"
                  )}
                </button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}

export default AssignQuizToBatch;
